<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";
import store from "@/store";

import { toast } from "@/common/utils/toast";
import { maskDateBR } from "@/common/utils/mask";

import {
  userAccountByMember,
  userProfile,
  userProfileParch,
} from "@/common/api/rest/user";

import {
  fileAvatarCreate,
  fileAvatarUpdate,
  fileAvatarDelete,
} from "@/common/api/rest/file/avatar";

import { PanelTheme } from "@/common/themes";

import {
  baseAvatar,
  baseConfirm,
  baseInput,
  baseSelect,
  baseTextarea,
} from "@/common/components";

const { t, tm } = useI18n();

const DEFAULT_PAYLOAD = {
  name: "",
  social_name: "",
  avatar: null,
  email: "",
  gender: null,
  birthdate: "",
  registration_number: "",
  description: "",
};

const profile = ref({ ...DEFAULT_PAYLOAD });
const account = ref<any>(null);

const showAvatarDel = ref(false);

const member = computed(() => store.getters.user);

const genderOptions = computed(() => [
  {
    option: "Masculino",
    value: "MALE",
  },
  {
    option: "Feminino",
    value: "FEMALE",
  },
  {
    option: "Outro",
    value: "OTHER",
  },
]);

function fecthAccount() {
  userAccountByMember(member.value?.member_id)
    .then((response) => {
      const { success, data, message } = response;
      if (success) {
        account.value = data;
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function fetchProfile() {
  if (!member.value) return;

  userProfile()
    .then((response) => {
      const { success, data, message } = response;

      if (success) {
        const {
          avatar,
          birthdate,
          name,
          social_name,
          registration_number,
          gender,
          description,
        } = data;

        profile.value = {
          avatar,
          birthdate,
          name,
          gender,
          social_name,
          description,
          registration_number,
          email: store.getters.user.email,
        };

        store.commit("SET_PROFILE", data);
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function update(field: string) {
  if (!member.value) return;

  let payload = { [field]: profile.value[field] };

  userProfileParch(payload)
    .then((response) => {
      const { success, message } = response;
      if (success) {
        fetchProfile();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function addAvatar(file: string) {
  if (!member.value || !account.value) return;

  fileAvatarCreate(account.value.account_id, {
    owner_id: member.value.member_id,
    type: "USER",
    file,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchProfile();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function updateAvatar(file: string) {
  if (!profile.value?.avatar || !account.value) return;

  fileAvatarUpdate(account.value.account_id, profile.value.avatar.avatar_id, {
    file,
  })
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchProfile();
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

function changeAvatar(avatar: string) {
  if (profile.value.avatar) {
    updateAvatar(avatar);
  } else {
    addAvatar(avatar);
  }
}

function delAvatar() {
  showAvatarDel.value = true;
}

function delAvatarConfirm() {
  if (!profile.value?.avatar || !account.value) return;

  fileAvatarDelete(account.value.account_id, profile.value.avatar.avatar_id)
    .then((response) => {
      const { success, message } = response;

      if (success) {
        fetchProfile();
        showAvatarDel.value = false;
        toast({ message: message, type: "success" });
      } else {
        toast({ message: message, type: "error" });
      }
    })
    .catch(() => {
      toast({ message: t("error.default"), type: "error" });
    });
}

const debounceUpdate = debounce(update, 1000);

onMounted(() => {
  fetchProfile();
  fecthAccount();
});
</script>

<template>
  <panel-theme>
    <article class="page-screen">
      <section class="page-content">
        <div class="form">
          <div class="flex justify-center">
            <base-avatar
              position="center"
              :is-edit="true"
              :show-image="true"
              :url="profile.avatar?.large"
              @change="changeAvatar($event)"
              @del="delAvatar()"
            />
          </div>

          <base-input
            v-model="profile.name"
            :value="profile.name"
            :placeholder="t('common.placeholders.name')"
            :required="true"
            :max-length="50"
            @update:modelValue="debounceUpdate('name')"
          >
            <template #label> {{ t("common.labels.name") }} * </template>
          </base-input>

          <base-input
            v-model="profile.email"
            :value="profile.email"
            :placeholder="t('common.placeholders.email')"
            :disabled="true"
            :required="true"
            :max-length="50"
          >
            <template #label>
              {{ t("common.labels.email") }}
            </template>
          </base-input>

          <base-input
            v-model="profile.registration_number"
            :value="profile.registration_number"
            :required="true"
            :max-length="75"
            @update:modelValue="debounceUpdate('registration_number')"
          >
            <template #label> Matrícula </template>
          </base-input>

          <base-input
            v-model="profile.birthdate"
            :value="profile.birthdate"
            :placeholder="t('common.placeholders.birthdate')"
            :required="true"
            :max-length="50"
            :mask="maskDateBR"
            @update:modelValue="debounceUpdate('birthdate')"
          >
            <template #label>
              {{ t("common.labels.birthdate") }}
            </template>
          </base-input>

          <base-select
            v-model="profile.gender"
            :value="profile.gender"
            :options="genderOptions"
            :is-empty="false"
            @change="update('gender')"
          >
            <template #label> Sexo </template>
          </base-select>

          <base-textarea
            v-model="profile.description"
            :value="profile.description"
            :placeholder="t('common.placeholders.describe')"
            :rows="5"
            @update:modelValue="debounceUpdate('description')"
          >
            <template #label>
              {{ t("common.labels.description") }}
            </template>
          </base-textarea>
        </div>
      </section>
    </article>
  </panel-theme>

  <base-confirm
    :show="showAvatarDel"
    :title="t('common.modals.titles.remove_avatar').toUpperCase()"
    :message="t('common.modals.messages.remove_avatar')"
    @cancel="showAvatarDel = false"
    @confirm="delAvatarConfirm()"
  />
</template>

<style lang="scss" scoped>
.page-screen {
  display: flex;
  flex-direction: column;
  width: 100%;

  .page-header {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    width: inherit;
    background: white;
    border-radius: 0.5rem;
    padding-block: 0.5rem;
  }

  .page-content {
    display: flex;
    flex-direction: column;
    margin-block-start: 1.54rem;
    margin-block-end: 1.54rem;

    .form {
      display: flex;
      flex-direction: column;
      gap: 1.54rem;
      background: white;
      padding: 1.54rem;
      border-radius: 0.5rem;
    }
  }
}
</style>
